<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { SectionThumbnailListRecord } from '~/types/datocms'
import { theme } from '#tailwind-config'

const breakpoints = useBreakpoints(theme.screens)
const isScreenLg = breakpoints.greater('lg')
const isScreenMd = breakpoints.greater('md')

const NuxtLink = resolveComponent('NuxtLink')

const props = defineProps<{
  data: SectionThumbnailListRecord
  moduleIndex: number
}>()

const { level, isSemantic } = useSectionRules(props.moduleIndex, !!props.data.title)
const store = useAppStore()

const count = computed(() =>
  props.data.variant === 'Projects' ? store.projectsCount : store.locationsCount
)

const { emitterEmit } = useEmitter()
const sectionRef = ref<HTMLElement | null>(null)
const { isOutside } = useMouseInElement(sectionRef)

const showImage = (index: number) => {
  if (isScreenLg.value) {
    return true
  }

  if (isScreenMd.value) {
    if (props.data.variant === 'Projects') {
      return index !== props.data.gallery.length - 1
    } else {
      return index !== props.data.gallery?.length - 1 && index !== props.data.gallery.length - 2
    }
  }
  if (props.data.variant === 'Projects') {
    return index !== props.data.gallery.length - 1
  } else {
    return true
  }
}

let triggeredOnce = false

watch(isOutside, (outside) => {
  if (!outside && !triggeredOnce) {
    triggeredOnce = true
    emitterEmit('hotspotEnter', {
      label: props.data.link?.title,
      iconRight: 'custom:chevron-right',
      inverted: true
    })
  }
})

const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

const calculateInitialTransform = (
  element: HTMLElement,
  offsetDistance = 250,
  maxRotation = 300,
  maxZTranslation = 2000
) => {
  const viewportCenter = { width: window.innerWidth / 2, height: window.innerHeight / 2 }
  const elementCenter = {
    x: element.offsetLeft + element.offsetWidth / 2,
    y: element.offsetTop + element.offsetHeight / 2
  }

  // Calculate the angle between the center of the element and the center of the viewport
  const angle = Math.atan2(
    Math.abs(viewportCenter.height - elementCenter.y),
    Math.abs(viewportCenter.width - elementCenter.x)
  )

  // Calculate the x and y translation based on the angle and distance
  const translateX = Math.abs(Math.cos(angle) * offsetDistance)
  const translateY = Math.abs(Math.sin(angle) * offsetDistance)

  // Calculate the maximum possible distance from the center (diagonal of the viewport)
  const maxDistance = Math.sqrt(
    Math.pow(viewportCenter.width, 2) + Math.pow(viewportCenter.height, 2)
  )

  // Calculate the current distance from the center
  const currentDistance = Math.sqrt(
    Math.pow(viewportCenter.width - elementCenter.x, 2) +
      Math.pow(viewportCenter.height - elementCenter.y, 2)
  )

  // Scale rotation and Z-translation based on distance from the center (closer elements rotate/translate less, farther ones rotate/translate more)
  const distanceFactor = currentDistance / maxDistance

  // Calculate the rotation values based on the position relative to the center
  const rotationX =
    (elementCenter.y < viewportCenter.height ? -1 : 1) *
    (translateY / offsetDistance) *
    maxRotation *
    distanceFactor
  const rotationY =
    (elementCenter.x < viewportCenter.width ? 1 : -1) *
    (translateX / offsetDistance) *
    maxRotation *
    distanceFactor

  // Calculate the Z-axis translation (depth) based on the distance from the center
  const translateZ = maxZTranslation * distanceFactor

  // Determine direction based on position relative to the viewport center
  return {
    x: elementCenter.x < viewportCenter.width ? -translateX : translateX,
    y: elementCenter.y < viewportCenter.height ? -translateY : translateY,
    z: translateZ,
    rotateX: rotationX,
    rotateY: rotationY
  }
}

onMounted(() => {
  nextTick(() => {
    if (ctxRef.value) {
      gsap.registerPlugin(ScrollTrigger)
      const gridImages = ctxRef.value?.querySelectorAll('.fx-thumbnail-image')
      ctx = gsap.context(() => {
        if (props.data.variant === 'Projects') {
          gsap
            .timeline({
              defaults: {
                ease: 'sine'
              },
              scrollTrigger: {
                trigger: ctxRef.value,
                start: 'center center',
                end: '+=150%',
                pin: ctxRef.value?.parentNode,
                scrub: 0.3
              }
            })
            .set(ctxRef.value, { perspective: 1000 })
            .from(gridImages, {
              stagger: {
                amount: 0.4,
                from: 'random',
                grid: [4, 9]
              },
              y: window.innerHeight,
              rotationX: -70,
              transformOrigin: '50% 0%',
              z: -900,
              autoAlpha: 0
            })
        } else {
          gsap
            .timeline({
              defaults: {
                ease: 'expo'
              },
              scrollTrigger: {
                trigger: ctxRef.value,
                start: 'center center',
                end: '+=150%',
                pin: ctxRef.value?.parentNode,
                scrub: 0.2
              }
            })
            .set(ctxRef.value, { perspective: 1000 }) // Add perspective for 3D effect
            .fromTo(
              gridImages,
              {
                // Define the starting position based on the pre-calculated translation, rotation, and Z-axis translation values
                x: (_, el) => calculateInitialTransform(el).x,
                y: (_, el) => calculateInitialTransform(el).y,
                z: (_, el) => calculateInitialTransform(el).z, // Z-axis translation
                rotateX: (_, el) => calculateInitialTransform(el).rotateX * 0.5,
                rotateY: (_, el) => calculateInitialTransform(el).rotateY,
                autoAlpha: 0,
                scale: 0.7
              },
              {
                // Animate the images to their original position and remove transform
                x: 0,
                y: 0,
                z: 0,
                rotateX: 0,
                rotateY: 0,
                autoAlpha: 1,
                scale: 1,
                stagger: {
                  amount: 0.2,
                  from: 'center',
                  grid: [4, 9]
                }
              }
            )
        }
        ScrollTrigger.refresh()
      }, ctxRef.value as HTMLElement)
    }
  })
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})

onBeforeUnmount(() => {
  emitterEmit('hotspotLeave')
})
</script>

<template>
  <div
    class="px-4 lg:px-8"
    :class="data.variant === 'Locations' ? 'pb-[392px] pt-4 lg:pb-[552px] lg:pt-8' : 'py-4 lg:py-8'"
  >
    <component
      :is="isScreenLg ? NuxtLink : 'section'"
      ref="sectionRef"
      :to="isScreenLg ? data.link?.recordLink?.slug : undefined"
      class="thumbnails-wrapper pointer-events-none relative cursor-none overflow-x-hidden bg-black text-yellow lg:pointer-events-auto"
      @mouseover="
        emitterEmit('hotspotEnter', {
          label: props.data.link?.title,
          iconRight: 'custom:chevron-right',
          inverted: true
        })
      "
      @mouseleave="emitterEmit('hotspotLeave')"
    >
      <div ref="ctxRef" :class="data.variant === 'Projects' ? 'fx-projects' : 'fx-locations'">
        <template v-for="(image, idx) in data.gallery" :key="idx">
          <span
            v-if="showImage(idx)"
            class="fx-thumbnail-image rounded-sm"
            :style="{ 'background-image': `url(${image.responsiveImage?.src})` }"
          >
          </span>
        </template>
        <Heading
          v-if="data.title"
          :size="2"
          class="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col font-light lg:flex-row lg:gap-2"
          :level="level"
          :is-semantic="isSemantic"
        >
          <span class="self-end text-lg font-normal lg:order-2 lg:self-auto lg:text-2xl"
            >( {{ count }} )</span
          >
          <span class="lg:order-1">{{ data.title }}</span>
        </Heading>
      </div>
    </component>
    <NuxtLink
      :to="data.link?.recordLink?.slug"
      class="mt-4 inline-flex items-center gap-4 rounded-full bg-yellow/20 px-4 py-2 uppercase text-yellow lg:hidden"
    >
      {{ data.link?.title }}
      <Icon name="custom:chevron-right" size="16" />
    </NuxtLink>
  </div>
</template>

<style lang="postcss">
.thumbnails-wrapper {
  @apply relative grid h-screen min-h-screen place-items-center content-center;

  grid-template-areas: 'main';
  grid-template-rows: 100%;
  grid-template-columns: 100%;
}

.fx-projects {
  @apply grid h-full w-full gap-2;

  grid-area: main;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(8, 1fr);

  @screen lg {
    grid-template-columns: repeat(5, 1fr);
    grid-template-rows: repeat(5, 1fr);
  }
}

.fx-locations {
  @apply grid h-full w-full gap-2;

  grid-area: main;
  grid-template-columns: repeat(3, 1fr);

  @screen md {
    grid-template-columns: repeat(4, 1fr);
  }

  @screen lg {
    grid-template-columns: repeat(6, 1fr);
  }
}

.fx-thumbnail-image {
  @apply bg-cover bg-[50%_50%] will-change-transform;
  transform: translateZ(0.1px);
}
</style>
